/**
 * Shared TY2026 layout-V2 recipient identity (TS-7958).
 *
 * 2026 upload/DB layouts carry RECIPIENT_ID_TYPE (char(1): '1'=EIN, '2'=SSN/ITIN/ATIN,
 * '3'=UNKNOWN — portal uploads may carry the spelled-out form) plus split person names
 * and business names. Records with that layout must file businesses as BusinessName +
 * TINSubmittedTypeCd=BUSINESS_TIN and individuals as split PersonName + INDIVIDUAL_TIN.
 * Records without it (pre-2026) keep each form's legacy name handling.
 */

type AnyRecord = Record<string, any>;

/**
 * The IRS name control for a business: the first four significant characters, uppercased.
 *
 * "Significant" is doing the work. BusinessNameControlTxt takes only upper letters, digits,
 * hyphens and ampersands - no spaces, no punctuation - so taking the first four characters
 * of the name as they stand fails the pattern whenever one of them is a space or a stop:
 * "GEM STONE" gave "GEM " and the IRS refused the whole transmission over it (TS-8533).
 */
export function businessNameControl(name: string): string {
  return (name || "").replace(/[^A-Za-z0-9&-]/g, "").substring(0, 4).toUpperCase();
}

/**
 * The IRS name control for a person: the first four letters of the SURNAME (Pub 1220, "Name
 * Control"), not of the first name. A Pub 1220 upload carries the payee's own name control in
 * the B record (RECIPIENT_NAME_CONTROL), and that is what the filer told the IRS, so it wins.
 *
 * Built from the first name, as it was, the control matched the IRS's TIN records for almost
 * no individual: filings went through, and the mismatch surfaced later as TIN notices.
 * Characters the element does not take (digits, spaces, apostrophes) are dropped first, so
 * "VAN DYKE" gives VAND and "O'NEIL" ONEI.
 */
export function personNameControl(record: AnyRecord, first: string, last: string): string {
  const clean = (value: unknown) => String(value ?? "").toUpperCase().replace(/[^A-Z-]/g, "");
  const supplied = clean(record.RECIPIENT_NAME_CONTROL).substring(0, 4);
  if (supplied) return supplied;
  const surname = /^UNKNOWN$/i.test(String(last).trim()) ? "" : clean(last);
  return (surname || clean(first)).substring(0, 4);
}

export function usesSplitNameLayout(record: AnyRecord): boolean {
  return !!(
    record.RECIPIENT_ID_TYPE ||
    record.RECIPIENT_FIRST_NAME ||
    record.RECIPIENT_BUSINESS_NAME1
  );
}

export function isBusinessRecipientIdentity(record: AnyRecord): boolean {
  const idType = (record.RECIPIENT_ID_TYPE || "").toString().trim().toUpperCase();
  if (
    idType === "1" ||
    idType === "E" ||
    idType === "EIN" ||
    idType.includes("BUSINESS")
  ) {
    return true;
  }
  return !!record.RECIPIENT_BUSINESS_NAME1?.trim();
}

/**
 * Build the split-layout RecipientDetail (business or person), or return null when
 * the record uses the legacy layout — the caller then keeps its existing shape.
 * `tin` and `mailingAddressGrp` are supplied by the caller so each form keeps its
 * own TIN-masking and address rules.
 */
export function buildSplitRecipientDetail(
  record: AnyRecord,
  tin: string,
  mailingAddressGrp: object
): object | null {
  if (!usesSplitNameLayout(record)) return null;

  if (isBusinessRecipientIdentity(record)) {
    const businessName =
      record.RECIPIENT_BUSINESS_NAME1?.trim() ||
      (record.RECIPIENT_NAME || "UNKNOWN").trim();
    return {
      TIN: tin,
      TINSubmittedTypeCd: "BUSINESS_TIN",
      BusinessNameControlTxt: businessNameControl(businessName),
      BusinessName: {
        BusinessNameLine1Txt: businessName,
        ...(record.RECIPIENT_BUSINESS_NAME2 && {
          BusinessNameLine2Txt: record.RECIPIENT_BUSINESS_NAME2,
        }),
      },
      MailingAddressGrp: mailingAddressGrp,
    };
  }

  const first = record.RECIPIENT_FIRST_NAME || record.RECIPIENT_NAME || "UNKNOWN";
  const last = record.RECIPIENT_LAST_NAME || record.RECIPIENT_NAME2 || "UNKNOWN";
  return {
    TIN: tin,
    TINSubmittedTypeCd: "INDIVIDUAL_TIN",
    PersonNameControlTxt: personNameControl(record, first, last),
    PersonName: {
      PersonFirstNm: first,
      ...(record.RECIPIENT_MIDDLE_NAME && {
        PersonMiddleNm: record.RECIPIENT_MIDDLE_NAME,
      }),
      PersonLastNm: last,
      ...(record.RECIPIENT_SUFFIX && { SuffixNm: record.RECIPIENT_SUFFIX }),
    },
    MailingAddressGrp: mailingAddressGrp,
  };
}

/**
 * The name the IRS already holds for the record a correction points at
 * (PrevSubmittedRecRecipientGrp). The schema takes PersonName or BusinessName and
 * needs PersonFirstNm before PersonLastNm, both present.
 *
 * A split-layout record (a Pub 1220 upload, a 2026 CSV with RECIPIENT_FIRST_NAME or
 * RECIPIENT_BUSINESS_NAME1) has no RECIPIENT_NAME, so building this group from the
 * legacy name columns left PersonFirstNm empty and the transmission failed schema
 * validation with "Element 'PersonLastNm': This element is not expected" (TS-8393).
 * Returns null for a legacy-layout record so the caller keeps its existing shape.
 */
export function buildPrevSubmittedRecipientName(
  record: AnyRecord
): { PersonName: object } | { BusinessName: object } | null {
  if (!usesSplitNameLayout(record)) return null;

  if (isBusinessRecipientIdentity(record)) {
    const businessName =
      record.RECIPIENT_BUSINESS_NAME1?.trim() ||
      (record.RECIPIENT_NAME || "UNKNOWN").trim();
    return {
      BusinessName: {
        BusinessNameLine1Txt: businessName,
        ...(record.RECIPIENT_BUSINESS_NAME2 && {
          BusinessNameLine2Txt: record.RECIPIENT_BUSINESS_NAME2,
        }),
      },
    };
  }

  return {
    PersonName: {
      PersonFirstNm: record.RECIPIENT_FIRST_NAME || record.RECIPIENT_NAME || "UNKNOWN",
      ...(record.RECIPIENT_MIDDLE_NAME && {
        PersonMiddleNm: record.RECIPIENT_MIDDLE_NAME,
      }),
      PersonLastNm: record.RECIPIENT_LAST_NAME || record.RECIPIENT_NAME2 || "UNKNOWN",
      ...(record.RECIPIENT_SUFFIX && { SuffixNm: record.RECIPIENT_SUFFIX }),
    },
  };
}
